import { ALLOCATION_STATUS } from './statuses';

// Per-status action visibility; shared with the orders slice's Allocation details tab.

// `approvalStatus` arrives as display text; normalising also tolerates the raw enum.
const normalize = (status) =>
  String(status ?? '')
    .toLowerCase()
    .replace(/_/g, ' ')
    .trim();

const STATUS = Object.freeze(
  Object.fromEntries(Object.entries(ALLOCATION_STATUS).map(([k, v]) => [k, normalize(v)])),
);

const isStatus =
  (...statuses) =>
  (row) =>
    statuses.includes(normalize(row.statusCode));

const isCreditsRetired = isStatus(STATUS.creditsRetired);
const isPendingShipment = isStatus(STATUS.pendingShipment);
const isRevoked = isStatus(STATUS.revoked);
const hasDeclaration = isStatus(STATUS.declarationGenerated, STATUS.declarationUpdated);

const not = (predicate) => (row) => !predicate(row);
const every =
  (...predicates) =>
  (row) =>
    predicates.every((predicate) => predicate(row));

export const ALLOCATION_ACTION_VISIBILITY = Object.freeze({
  updateShipmentDetails: isPendingShipment,
  linkExistingShipment: isPendingShipment,
  generateDeclaration: every(
    not(isCreditsRetired),
    not(isRevoked),
    not(isPendingShipment),
    (row) => row.canGenerateDeclaration,
  ),
  downloadDeclaration: every(not(isCreditsRetired), hasDeclaration),
  editDeclaration: every(not(isCreditsRetired), isStatus(STATUS.declarationGenerated)),
  // The preview's Edit: open until the one edit after generating is used; only that edit warns first.
  editFromPreview: every(
    not(isCreditsRetired),
    isStatus(STATUS.pendingDeclaration, STATUS.declarationGenerated),
  ),
  addAttachments: every(not(isCreditsRetired), isStatus(STATUS.pendingDeclaration)),

  viewDeclaration: isStatus(
    STATUS.declarationGenerated,
    STATUS.declarationUpdated,
    STATUS.declarationRejected,
    STATUS.revoked,
  ),
  // The server rejects only a generated declaration, never an updated one.
  markRejected: isStatus(STATUS.declarationGenerated),
  addAttachmentsMenu: isStatus(
    STATUS.declarationGenerated,
    STATUS.declarationUpdated,
    STATUS.pendingShipment,
    STATUS.creditsRetired,
  ),
  revoke: isStatus(
    STATUS.pendingShipment,
    STATUS.pendingDeclaration,
    STATUS.declarationGenerated,
    STATUS.declarationUpdated,
    STATUS.creditsRetired,
  ),
});

export const hiddenUnless = (isVisible) => (row) => !isVisible(row);
